import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useSnackbar } from "notistack";
import { Box, IconButton, Link, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";

import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "@accredo/fe-shared/components/tables/hooks/useMultiTableData.jsx";
import { useDebounce } from "@accredo/fe-shared/hooks/useDebounce.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import {
  getDatum,
  getFileSize,
} from "@accredo/fe-shared/services/formatService.js";
import { Button } from "@accredo/fe-shared/theme/components";

import DocumentPreview from "./DocumentPreview.jsx";
import SearchField from "./SearchField.jsx";
import DocumentCardList from "./DocumentCardList.jsx";
import { labelForMimetype } from "./documentFileIcons.js";
import {
  downloadDocument,
  downloadSelectedDocuments,
} from "../utils/downloadDocument.js";
import { metaForType } from "../constants/documentTypeMeta.js";

// A configured DocumentType keeps the order the API returns (sort_order);
// any other type value (an unmapped KATEGORIE1 passthrough) sorts after
// them, alphabetically, so the tab order stays stable across reloads.
function sortTypes(types, documentTypes) {
  const order_of = (slug) =>
    documentTypes.findIndex((document_type) => document_type.slug === slug);

  return [...types].sort((a, b) => {
    const index_a = order_of(a);
    const index_b = order_of(b);

    if (index_a === -1 && index_b === -1) {
      return a.localeCompare(b);
    }
    if (index_a === -1) return 1;
    if (index_b === -1) return -1;

    return index_a - index_b;
  });
}

/**
 * The portal's document list: server-side filtering/sorting/paging via
 * DataTable/useMultiTableData (the same pattern PortalContractsTable uses),
 * type tabs built from a single GET /data/documents/type-counts request
 * (no fixed list of types), and the row-click preview.
 *
 * With a `vertragId` it becomes the documents card of one Vertrag: every
 * request (list and counts) carries filter[vertrag_id], the Vertrag/Objekt
 * column is left out, and the tab is local state rather than `?type=`.
 * `objekt` ({ id, bezeichnung }) additionally narrows list and counts to the
 * documents attached to that Objekt, with a note and `onResetObjekt` to lift
 * it. Every change of `objektSelection` (one per Objekt the user picks, the
 * same one again included) puts the type tab back on Alle; lifting the
 * filter leaves the type tab as it is.
 */
export default function PortalDocumentsTable({
  vertragId = null,
  title = "Dokumente",
  selectable = false,
  objekt = null,
  objektSelection = 0,
  onResetObjekt = null,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const is_mobile = useMediaQuery(theme.breakpoints.down("md"));
  const [previewDocument, setPreviewDocument] = useState(null);
  const [typeCounts, setTypeCounts] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [localType, setLocalType] = useState("all");
  const [rowSelection, setRowSelection] = useState({});
  const objektId = objekt?.id ?? null;
  const [seenSelection, setSeenSelection] = useState(objektSelection);
  if (seenSelection !== objektSelection) {
    // Adjusted during render so no request goes out with the new Objekt and
    // the previous type.
    setSeenSelection(objektSelection);
    setLocalType("all");
  }
  const storagePrefix = vertragId
    ? "kp_table_contract_documents"
    : "kp_table_documents";
  const scopeFilter = useMemo(
    () => ({
      ...(vertragId && { vertrag_id: vertragId }),
      ...(objektId && { objekt_id: objektId }),
    }),
    [vertragId, objektId]
  );

  // Loaded once per scope - the breakdown doesn't depend on which tab is
  // active.
  useEffect(() => {
    backendGet("/data/documents/type-counts", {
      label: "Dokumenttypen",
      enqueueSnackbar,
      options: { params: { filter: scopeFilter } },
    }).then((response) => {
      if (response?.data) {
        setTypeCounts(response.data);
        setDocumentTypes(response.document_types ?? []);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeFilter]);

  const availableTypes = useMemo(
    () =>
      typeCounts
        ? sortTypes(
            Object.keys(typeCounts).filter((key) => key !== "all"),
            documentTypes
          )
        : [],
    [typeCounts, documentTypes]
  );

  // An unknown type value (or none) opens Alle rather than an empty page.
  // Before the counts have loaded there is nothing to validate against yet,
  // so the requested value is trusted provisionally - the main request
  // below already carries it either way (AC 5) - and corrected once the
  // counts arrive if it turns out not to be a real type.
  const requestedType = vertragId ? localType : searchParams.get("type");
  const activeType =
    requestedType && (typeCounts === null || requestedType in typeCounts)
      ? requestedType
      : "all";
  // Scoped to one Vertrag (the contract detail page's documents card): no
  // URL-driven SearchField there (no useful URL state for an embedded card),
  // so it searches through the table's own built-in global filter instead
  // (see enableGlobalFilter/onChangeGlobalFilter below).
  const [embeddedSearch, setEmbeddedSearch] = useState("");
  const activeSearch = vertragId
    ? embeddedSearch
    : searchParams.get("search") || "";

  // Each tab and Objekt filter gets its own slot in the table state, so a
  // response for one can never land under another: a late-resolving request
  // for a tab or Objekt that's no longer active writes into a slot nothing
  // reads from, and one that has never loaded starts from empty rows rather
  // than showing whatever the previous one left behind.
  const tableKey = `documents_${objektId ?? "all"}_${activeType}`;
  const sortingRef = useRef([]);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Dokumente",
        route: "/data/documents",
        pageSize: 10,
        storageKey: storagePrefix,
        extraParams: {
          include: ["vertrag", "objekt"],
          sort: [{ field: "document_date", order: "desc" }],
          filters: {
            ...scopeFilter,
            ...(activeType !== "all" && { type: activeType }),
            ...(activeSearch && { _searchword: activeSearch }),
          },
        },
      },
    ],
    [activeType, tableKey, scopeFilter, storagePrefix, activeSearch]
  );

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  // Initial load and every tab change go through the hook's own fetch path -
  // shouldFetch must be explicit (see PortalContractsTable for the same
  // pattern). This also covers arriving with ?type= already set: tablesConfig
  // already reflects it on the first render.
  useEffect(() => {
    setRowSelection({});
    updateTableState(tableKey, {
      pageIndex: 0,
      sorting: sortingRef.current,
      shouldFetch: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType, activeSearch, objektId]);

  const handleTabChange = (value) => {
    if (vertragId) {
      setLocalType(value);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") {
          next.delete("type");
        } else {
          next.set("type", value);
        }
        return next;
      },
      { replace: true }
    );
  };

  // Backs the page-level SearchField below (standalone /dokumente, and the
  // embedded card's mobile field - the embedded card's desktop field goes
  // through DataTable's own global filter instead, which MaterialReactTable
  // already debounces itself). Typing stays instant (local state); the
  // commit - URL param or embeddedSearch, whichever activeSearch reads from -
  // only updates once typing pauses.
  const [searchInput, setSearchInput] = useState(activeSearch);
  const debouncedSearchInput = useDebounce(searchInput, 300);
  const isFirstSearchRun = useRef(true);
  useEffect(() => {
    if (isFirstSearchRun.current) {
      isFirstSearchRun.current = false;
      return;
    }
    if (vertragId) {
      setEmbeddedSearch(debouncedSearchInput);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearchInput) {
          next.set("search", debouncedSearchInput);
        } else {
          next.delete("search");
        }
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  const filterTabs = [
    { key: "all", label: "Alle", count: typeCounts?.all ?? 0 },
    ...availableTypes.map((type) => ({
      key: type,
      label: metaForType(type, documentTypes).label,
      count: typeCounts[type] ?? 0,
    })),
  ].map((tab) => ({ ...tab, onClick: handleTabChange }));

  const columns = [
    {
      accessorKey: "bezeichnung",
      header: "Dokument",
      // Widest column on desktop - the document's own name/label is what the
      // user scans this table for, so it gets the most room; the others
      // (type, Vertrag/Objekt, date, download) are short, fixed-shape values.
      size: 320,
      Cell: ({ row }) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {row.original.bezeichnung}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {labelForMimetype(row.original.mimetype)} ·{" "}
            {getFileSize(row.original.filesize)}
          </Typography>
        </Box>
      ),
    },
    {
      id: "type",
      header: "Typ",
      size: 120,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => metaForType(row.original.type, documentTypes).label,
    },
    !vertragId && {
      id: "vertrag_objekt",
      header: "Vertrag / Objekt",
      size: 180,
      // Neither the Objekt's name nor the Vertragsnummer is a single
      // sortable server field for this column - see the same note on
      // PortalContractsTable's objekt_vertrag column.
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const { vertrags, objekt } = row.original;

        return (
          <Box>
            {objekt && (
              <Typography variant="body2" fontWeight={600}>
                {objekt.bezeichnung}
              </Typography>
            )}
            {/* A collective invoice is linked to several Verträge - each gets
                its own clickable Vertragsnummer rather than picking one. */}
            {(vertrags || []).map((vertrag) => (
              <Link
                key={vertrag.id}
                component="button"
                variant={objekt ? "caption" : "body2"}
                sx={{ display: "block", width: "fit-content" }}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/vertraege/${vertrag.id}`);
                }}
              >
                {vertrag.vertragsnummer}
              </Link>
            ))}
          </Box>
        );
      },
    },
    {
      accessorKey: "document_date",
      header: "Datum",
      size: 110,
      Cell: ({ row }) => getDatum(row.original.document_date),
    },
    {
      id: "download",
      header: "",
      size: 60,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <IconButton
          aria-label="Download"
          onClick={(event) => {
            event.stopPropagation();
            downloadDocument(row.original, { enqueueSnackbar });
          }}
        >
          <DownloadOutlined />
        </IconButton>
      ),
    },
  ].filter(Boolean);

  const handleChangePage = (nextOrUpdater) => {
    if (!tableStates[tableKey]) {
      return;
    }
    const base = {
      pageIndex: tableState.pageIndex ?? 0,
      pageSize: tableState.pageSize ?? 10,
    };
    const next =
      typeof nextOrUpdater === "function" ? nextOrUpdater(base) : nextOrUpdater;
    if (!next) {
      return;
    }
    setRowSelection({});
    updateTableState(tableKey, {
      pageIndex: next.pageIndex,
      pageSize: next.pageSize,
    });
  };

  const handleVertragClick = (vertrag) => navigate(`/vertraege/${vertrag.id}`);

  const selectedIds = Object.keys(rowSelection).filter(
    (id) => rowSelection[id]
  );

  const downloadSelected = () =>
    downloadSelectedDocuments(selectedIds, { enqueueSnackbar });

  const toolbarActions =
    selectable && selectedIds.length > 0
      ? [
          {
            type: "button",
            label: "Ausgewählte herunterladen",
            icon: <DownloadOutlined />,
            variant: "primary",
            onClick: downloadSelected,
          },
        ]
      : [];

  return (
    <>
      {/* The embedded card searches through DataTable's own global filter
          on desktop; the mobile card list has none, so it gets this field. */}
      {(!vertragId || is_mobile) && (
        <SearchField
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          isLoading={tableState.isLoading}
        />
      )}

      {objektId && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2">
            {`Vorgefiltert nach Objekt: ${objekt.bezeichnung}`}
          </Typography>
          <Button variant="neutral" size="small" onClick={onResetObjekt}>
            Zurücksetzen
          </Button>
        </Stack>
      )}
      {is_mobile ? (
        <DocumentCardList
          rows={rows}
          isLoading={tableState.isLoading}
          hasLoaded={tableState.hasLoaded}
          pageIndex={tableState.pageIndex}
          pageSize={tableState.pageSize}
          totalRows={tableState.totalRows}
          title={`${title} (${tableState.totalRows ?? 0})`}
          onChangePage={handleChangePage}
          onRowClick={(row) => setPreviewDocument(row)}
          showVertragObjekt={!vertragId}
          onVertragClick={handleVertragClick}
          filterTabs={filterTabs}
          activeFilterTab={activeType}
          selectable={selectable}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          onDownloadSelected={downloadSelected}
          documentTypes={documentTypes}
        />
      ) : (
        <DataTable
          testId="portal-documents-table"
          columns={columns}
          data={rows}
          isLoading={tableState.isLoading}
          hasLoaded={tableState.hasLoaded}
          pageIndex={tableState.pageIndex}
          pageSize={tableState.pageSize}
          totalRows={tableState.totalRows}
          title={`${title} (${tableState.totalRows ?? 0})`}
          onChangeSorting={(sorting) => {
            // A brand new tab's slot is only added once useMultiTableData's
            // own effect runs, so it can still be missing during the render
            // right after switching to a tab that has never been open before.
            if (!tableStates[tableKey]) {
              return;
            }
            sortingRef.current = sorting;
            setRowSelection({});
            updateTableState(tableKey, { sorting });
          }}
          onChangePage={handleChangePage}
          storageKey={storagePrefix}
          enableSorting
          enableColumnFilters={false}
          enableGlobalFilter={Boolean(vertragId)}
          onChangeGlobalFilter={setEmbeddedSearch}
          enableColumnHiding={false}
          filterTabs={filterTabs}
          activeFilterTab={activeType}
          onRowClick={(row) => setPreviewDocument(row)}
          toolbarActions={toolbarActions}
          {...(selectable
            ? {
                enableRowSelection: true,
                rowSelection,
                onRowSelectionChange: setRowSelection,
                getRowId: (row) => String(row.id),
              }
            : {})}
        />
      )}

      {tableState.hasLoaded && !tableState.isLoading && rows.length === 0 && (
        <Typography sx={{ mt: 2 }} color="text.secondary">
          {objektId
            ? "Keine objektspezifischen Dokumente vorhanden"
            : "Keine Dokumente vorhanden."}
        </Typography>
      )}

      <DocumentPreview
        open={Boolean(previewDocument)}
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </>
  );
}
